/**
 * SEO / GEO MİMARİ AJANI — sitenin şeklini gösterir.
 *
 * Teknik SEO ajanı sayfa hijyenine bakar; bu ekran SİTENİN TAMAMINA bakar:
 * hangi sayfaya kaç tıkta ulaşılıyor, otorite nereye akıyor, konular ayrı
 * silolarda mı. Tamamen mekanik hesap — AI çağrısı yok, maliyeti yok, anında
 * döner. O yüzden "tara" butonu da yok: sayfa açıldığında ölçüm hazır.
 */
import { useQuery } from "@tanstack/react-query";
import { useListDomains } from "@workspace/api-client-react";
import { useActiveDomain } from "@/hooks/use-active-domain";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, Waypoints, Info } from "lucide-react";

type Durum = "gecti" | "eksik" | "kritik" | "bilinmiyor";
type Kontrol = {
  id: string; sutun: string; baslik: string; aranan: string; bulunan: string;
  durum: Durum; yapilacak?: string; ornekler?: { url: string; not?: string }[];
};
type Sutun = { sutun: string; ad: string; puan: number | null };
type Sonuc = {
  hazir: boolean; mesaj?: string; genelPuan: number; sutunlar: Sutun[]; kontroller: Kontrol[];
  ozet: {
    sayfa: number; saglikli: number; link: number; kume: number;
    derinlikDagilimi: Record<string, number> | null;
    ulasilmaz: number | null; siloButunlugu: number | null;
  };
};

const DURUM_STIL: Record<Durum, { etiket: string; sinif: string }> = {
  gecti: { etiket: "TAMAM", sinif: "bg-success-50 text-success-700 dark:bg-success-500/15 dark:text-success-400" },
  eksik: { etiket: "EKSİK", sinif: "bg-warning-50 text-warning-700 dark:bg-warning-500/15 dark:text-warning-400" },
  kritik: { etiket: "KRİTİK", sinif: "bg-error-50 text-error-600 dark:bg-error-500/15 dark:text-error-400" },
  bilinmiyor: { etiket: "VERİ YOK", sinif: "bg-accent text-muted-foreground" },
};
const puanRengi = (p: number) =>
  p >= 75 ? "text-success-600 dark:text-success-400" : p >= 50 ? "text-warning-700 dark:text-warning-400" : "text-error-600 dark:text-error-400";
const SIRA: Record<Durum, number> = { kritik: 0, eksik: 1, bilinmiyor: 2, gecti: 3 };
// Sunucu beklenmedik bir durum gönderirse sayfa ÇÖKMESİN. Schema ekranında tam
// bu sınıf hata (tanımsız alana erişim) sayfayı beyaz ekrana düşürmüştü.
const stil = (d: Durum) => DURUM_STIL[d] ?? DURUM_STIL.bilinmiyor;
const sirala = (d: Durum) => SIRA[d] ?? 2;

/** Tık derinliği dağılımı — mimarinin tek bakışta görünen hâli. */
function DerinlikCubugu({ dagilim, ulasilmaz }: { dagilim: Record<string, number>; ulasilmaz: number }) {
  const sira = ["0", "1", "2", "3", "4", "5+"];
  const veri = sira.map((k) => ({ k, n: dagilim[k] ?? 0 }));
  const toplam = veri.reduce((s, x) => s + x.n, 0) + ulasilmaz;
  if (toplam === 0) return null;
  // Yüzeye yakın yeşil, derinleştikçe kırmızı; ulaşılamaz ayrı ve en koyu.
  const renk = ["#16a34a", "#4ade80", "#a3e635", "#facc15", "#fb923c", "#ef4444"];
  return (
    <div>
      <div className="flex h-7 rounded-lg overflow-hidden border" style={{ borderColor: "#e7e7e4" }}>
        {veri.map((x, i) => x.n > 0 && (
          <div key={x.k} title={`${x.k} tık: ${x.n} sayfa`} style={{ width: `${(x.n / toplam) * 100}%`, background: renk[i] }}
            className="flex items-center justify-center text-[10px] font-semibold text-white/95 overflow-hidden">
            {(x.n / toplam) > 0.06 ? x.k : ""}
          </div>
        ))}
        {ulasilmaz > 0 && (
          <div title={`Ulaşılamıyor: ${ulasilmaz} sayfa`} style={{ width: `${(ulasilmaz / toplam) * 100}%`, background: "#7f1d1d" }}
            className="flex items-center justify-center text-[10px] font-semibold text-white/95 overflow-hidden">
            {(ulasilmaz / toplam) > 0.06 ? "yok" : ""}
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5 text-[10px] text-muted-foreground">
        {veri.map((x, i) => x.n > 0 && (
          <span key={x.k} className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-sm" style={{ background: renk[i] }} />{x.k} tık: {x.n}
          </span>
        ))}
        {ulasilmaz > 0 && (
          <span className="flex items-center gap-1 font-medium">
            <span className="w-2 h-2 rounded-sm" style={{ background: "#7f1d1d" }} />ulaşılamıyor: {ulasilmaz}
          </span>
        )}
      </div>
    </div>
  );
}

function Kontroller({ kontroller }: { kontroller: Kontrol[] }) {
  const sirali = [...kontroller].sort((a, b) => sirala(a.durum) - sirala(b.durum));
  return (
    <div className="divide-y divide-border">
      {sirali.map((k) => (
        <div key={k.id} className="py-2.5">
          <div className="flex items-start gap-2 flex-wrap">
            <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold shrink-0 ${stil(k.durum).sinif}`}>
              {stil(k.durum).etiket}
            </span>
            <span className="text-sm font-medium">{k.baslik}</span>
            <span className="text-[11px] text-muted-foreground">{k.bulunan}</span>
          </div>
          {k.yapilacak && <p className="text-[12px] text-muted-foreground mt-1 pl-1">→ {k.yapilacak}</p>}
          {/* Somut sayfalar: "235 sayfa sorunlu" demek yetmez, hangileri olduğu görünmeli.
              min-w-0: truncate'in çalışması için flex çocuğunda şart — yoksa uzun
              Arapça/kodlu yollar satırı taşırıyor. */}
          {k.ornekler && k.ornekler.length > 0 && (
            <ul className="mt-1.5 pl-1 space-y-0.5">
              {k.ornekler.map((o, i) => (
                <li key={i} className="text-[11px] border-l-2 border-border pl-2 flex gap-2 items-baseline">
                  <span className="font-mono truncate min-w-0 flex-1" title={o.url}>{o.url}</span>
                  {o.not && <span className="text-muted-foreground shrink-0 whitespace-nowrap">{o.not}</span>}
                </li>
              ))}
            </ul>
          )}
          <p className="text-[10px] text-muted-foreground mt-0.5 pl-1 opacity-70">aranan: {k.aranan}</p>
        </div>
      ))}
    </div>
  );
}

export default function MimariAgent() {
  const { data: domains } = useListDomains();
  const [activeDomainId] = useActiveDomain(domains);

  const { data, isLoading } = useQuery({
    queryKey: ["mimari", activeDomainId],
    enabled: !!activeDomainId,
    queryFn: async (): Promise<Sonuc> => {
      const r = await fetch(`/api/domains/${activeDomainId}/mimari`);
      if (!r.ok) throw new Error("Mimari denetimi alınamadı");
      return r.json();
    },
  });

  return (
    <div className="px-4 sm:px-6 py-6 w-full max-w-(--breakpoint-2xl) mx-auto space-y-4">
      <div>
        <h1 className="text-xl font-bold flex items-center gap-2">
          <Waypoints className="w-5 h-5 text-brand-500" /> SEO / GEO Mimari Ajanı
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Sitenin şekli: sayfalara kaç tıkta ulaşılıyor, iç otorite nereye akıyor, konular ayrı silolarda mı.
        </p>
      </div>

      <Card className="border-border bg-accent/40"><CardContent className="p-3 flex items-start gap-2">
        <Info className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
        <p className="text-[12px] text-muted-foreground">
          Bu denetim <b>tamamen mekanik</b>: taranmış sayfalar ve iç link grafiği üzerinden hesaplanır.
          AI çağrısı ve yeniden tarama yoktur, <b>hiçbir maliyeti yoktur</b>, her açtığınızda anında güncel gelir.
          Teknik SEO Ajanı tek tek sayfaların hijyenine bakar; burası sayfalar arasındaki <b>yapıya</b> bakar.
        </p>
      </CardContent></Card>

      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Mimari hesaplanıyor…
        </div>
      )}

      {data && !data.hazir && (
        <Card><CardContent className="p-4 text-sm text-muted-foreground">{data.mesaj}</CardContent></Card>
      )}

      {data?.hazir && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Card className="border-brand-500/40"><CardContent className="p-3">
              <div className={`text-2xl font-bold tabular-nums ${puanRengi(data.genelPuan)}`}>{data.genelPuan}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">Genel</div>
            </CardContent></Card>
            {(data.sutunlar ?? []).map((s) => (
              <Card key={s.sutun}><CardContent className="p-3">
                <div className={`text-2xl font-bold tabular-nums ${s.puan == null ? "text-muted-foreground" : puanRengi(s.puan)}`}>
                  {s.puan ?? "—"}
                </div>
                <div className="text-[11px] text-muted-foreground mt-0.5">{s.ad}</div>
              </CardContent></Card>
            ))}
          </div>

          <Card><CardContent className="p-4 space-y-3">
            <div className="flex flex-wrap gap-x-5 gap-y-1 text-[12px]">
              <span><b className="tabular-nums">{data.ozet.saglikli}</b> <span className="text-muted-foreground">sağlıklı sayfa</span></span>
              <span><b className="tabular-nums">{data.ozet.link}</b> <span className="text-muted-foreground">iç link</span></span>
              <span><b className="tabular-nums">{data.ozet.kume}</b> <span className="text-muted-foreground">konu kümesi</span></span>
              {data.ozet.siloButunlugu != null && (
                <span><b className="tabular-nums">%{data.ozet.siloButunlugu}</b> <span className="text-muted-foreground">silo bütünlüğü</span></span>
              )}
            </div>
            {data.ozet.derinlikDagilimi ? (
              <div>
                <div className="text-[11px] font-medium mb-1.5">Ana sayfadan tık derinliği</div>
                <DerinlikCubugu dagilim={data.ozet.derinlikDagilimi} ulasilmaz={data.ozet.ulasilmaz ?? 0} />
              </div>
            ) : (
              <p className="text-[11px] text-muted-foreground">Ana sayfa taranmadığı için derinlik ölçülemedi.</p>
            )}
          </CardContent></Card>

          <Card><CardContent className="p-4">
            <Kontroller kontroller={data.kontroller ?? []} />
          </CardContent></Card>
        </>
      )}
    </div>
  );
}
